<template>
  <div class="d-flex flex-column flex-lg-row">
    <div v-background-3 class="p-3 w-100 rounded mb-3">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <h5 class="m-0">Gestion des types de matériel</h5>
        <button class="btn btn-primary" @click="openModal">
          Ajouter un type de matériel
        </button>
      </div>

      <!-- Champ de recherche et options de tri -->
      <div class="d-flex flex-wrap justify-content-between mb-3">
        <div class="input-group mb-2 mr-sm-2" style="max-width: 300px;">
          <div class="input-group-prepend">
            <div class="input-group-text">
              <BaseIcon
                name="search"
                color="#333333"
                width="20"
                height="20"
              ></BaseIcon>
            </div>
          </div>
          <input
            type="text"
            class="form-control"
            placeholder="Rechercher..."
            v-model="searchQuery"
            @input="applySearch"
          />
        </div>

        <div class="d-flex align-items-center">
          <label class="mr-2 mb-0">Trier par :</label>
          <select
            class="form-control form-control-sm mr-2"
            v-model="sortField"
            @change="applySort"
          >
            <option value="description">Description</option>
          </select>

          <button
            class="btn btn-sm"
            :class="sortOrder === 'asc' ? 'btn-primary' : 'btn-outline-primary'"
            @click="toggleSortOrder"
          >
            <i
              :class="
                sortOrder === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'
              "
            ></i>
            {{ sortOrder === "asc" ? "ASC" : "DESC" }}
          </button>
        </div>
      </div>

      <div v-if="loading" class="d-flex justify-content-center">
        <Loader />
      </div>
      <div v-else>
        <table class="table table-striped">
          <thead>
            <tr>
              <th @click="setSortField('description')" class="sortable-header">
                Description
                <i
                  v-if="sortField === 'description'"
                  :class="
                    sortOrder === 'asc'
                      ? 'fas fa-sort-up ml-1'
                      : 'fas fa-sort-down ml-1'
                  "
                ></i>
              </th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="type in filteredAndSortedTypes" :key="type.id">
              <td>{{ type.description }}</td>
              <td class="text-right">
                <div class="btn-group">
                  <button
                    class="btn btn-sm btn-warning"
                    @click="editType(type)"
                  >
                    Modifier
                  </button>
                  <button
                    class="btn btn-sm btn-danger ml-2"
                    @click="deleteType(type.id)"
                  >
                    Supprimer
                  </button>
                </div>
              </td>
            </tr>
            <!-- Message quand aucun résultat trouvé -->
            <tr v-if="filteredAndSortedTypes.length === 0">
              <td colspan="2" class="text-center">
                <em>Aucun type de matériel ne correspond à votre recherche</em>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Compteur d'éléments -->
        <div class="text-muted small">
          {{ filteredAndSortedTypes.length }} type(s) sur
          {{ types.length }}
        </div>
      </div>
    </div>

    <!-- Modal pour ajouter/modifier un type -->
    <modal
      ref="typeModal"
      title="Type de Matériel"
      subtitle="Ajouter ou modifier un type de matériel"
      id="modal_materiel_type"
      @save="saveType"
      @close="closeModal"
      btn=""
      :btns="false"
    >
      <form @submit.prevent="saveType">
        <div class="form-group">
          <label for="description">Description</label>
          <input
            type="text"
            id="description"
            v-model="form.description"
            class="form-control"
            required
          />
        </div>
        <button type="submit" class="btn btn-primary">Enregistrer</button>
      </form>
    </modal>
  </div>
</template>

<script>
import Loader from "@/components/bases/Loader.vue";
import Modal from "@/components/bases/Modal.vue";
import BaseIcon from "@/components/bases/Icon.vue";

export default {
  components: {
    Loader,
    Modal,
    BaseIcon
  },
  data() {
    return {
      types: [], // Liste des types de matériel
      loading: false,
      form: {
        id: null,
        description: ""
      },
      // Propriétés pour la recherche et le tri
      searchQuery: "",
      sortField: "description",
      sortOrder: "asc",
      filteredTypes: [] // Types filtrés
    };
  },
  computed: {
    filteredAndSortedTypes() {
      return this.filteredTypes;
    }
  },
  methods: {
    closeModal() {
      this.resetForm();
      this.$refs.typeModal.hide();
    },

    // Récupérer les types de matériel
    fetchTypes() {
      this.loading = true;
      this.$http
        .get("/admin/materiel_type")
        .then(response => {
          this.types = response.data;
          this.applySearch();
        })
        .catch(error => console.error(error))
        .finally(() => {
          this.loading = false;
        });
    },

    // Filtrer les types selon la recherche
    applySearch() {
      const query = this.searchQuery.toLowerCase().trim();

      if (!query) {
        this.filteredTypes = [...this.types];
      } else {
        this.filteredTypes = this.types.filter(type => {
          return (
            type.description && type.description.toLowerCase().includes(query)
          );
        });
      }

      this.applySort();
    },

    // Trier les types
    applySort() {
      this.filteredTypes.sort((a, b) => {
        const valueA = a[this.sortField] || "";
        const valueB = b[this.sortField] || "";

        if (typeof valueA === "string") {
          return this.sortOrder === "asc"
            ? valueA.localeCompare(valueB)
            : valueB.localeCompare(valueA);
        } else {
          return this.sortOrder === "asc" ? valueA - valueB : valueB - valueA;
        }
      });
    },

    // Définir le champ de tri
    setSortField(field) {
      if (this.sortField === field) {
        this.toggleSortOrder();
      } else {
        this.sortField = field;
        this.sortOrder = "asc";
      }
      this.applySort();
    },

    // Inverser l'ordre de tri
    toggleSortOrder() {
      this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      this.applySort();
    },

    // Ouvrir le modal pour ajouter un type
    openModal() {
      this.resetForm();
      this.$refs.typeModal.show();
    },

    // Remplir le formulaire pour modifier un type
    editType(type) {
      this.form = { ...type };
      this.$refs.typeModal.show();
    },

    // Réinitialiser le formulaire
    resetForm() {
      this.form = {
        id: null,
        description: ""
      };
    },

    // Enregistrer un type (ajout ou modification)
    saveType() {
      const url = this.form.id
        ? `/admin/materiel_type/${this.form.id}`
        : `/admin/materiel_type`;
      const method = this.form.id ? "put" : "post";

      this.$http[method](url, this.form)
        .then(() => {
          this.$refs.typeModal.hide();
          this.fetchTypes();
          // Message de confirmation
          this.$toast.success(
            this.form.id
              ? "Type de matériel modifié avec succès"
              : "Type de matériel ajouté avec succès"
          );
        })
        .catch(error => {
          console.error(error);
          // Message d'erreur
          this.$toast.error("Une erreur est survenue lors de l'enregistrement");
        });
    },

    // Supprimer un type
    deleteType(id) {
      if (
        confirm(
          "Êtes-vous sûr de vouloir supprimer ce type de matériel ?\n\n ATTENTION : Assurez vous qu'il n'est pas utilisé par des matériels existants avant de le supprimer."
        )
      ) {
        this.$http
          .delete(`/admin/materiel_type/${id}`)
          .then(() => {
            this.fetchTypes();
            this.$toast.success("Type de matériel supprimé avec succès");
          })
          .catch(error => {
            console.error(error);
            this.$toast.error(
              "Impossible de supprimer ce type car il est utilisé par des matériels"
            );
          });
      }
    }
  },
  mounted() {
    this.fetchTypes();
  }
};
</script>

<style scoped>
/* Styles pour les en-têtes de colonnes triables */
.sortable-header {
  cursor: pointer;
  user-select: none;
  position: relative;
}

.sortable-header:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

/* Animation de transition pour le tri */
.table tbody tr {
  transition: all 0.3s ease;
}

/* Style pour l'icône de recherche */
.input-group-text {
  background-color: #f8f9fa;
  border-right: none;
  height: 38px;
}

input.form-control:focus + .input-group-prepend .input-group-text {
  border-color: #80bdff;
}
</style>
